import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, Wallet, Ban, CheckCircle2, ShieldCheck, ShieldOff } from "lucide-react";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate } from "@/lib/format";

export default function AdminMembers() {
  const { t } = useI18n();
  const [members, setMembers] = useState(null);
  const [sel, setSel] = useState(null);
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!members) return <Loader />;

  const adjust = async () => {
    try {
      await api.put(`/admin/members/${sel.id}/balance`, { amount: Number(amt), reason: reason || "Admin adjustment" });
      toast.success("Balance updated"); setSel(null); setAmt(""); setReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleLock = async (m) => {
    try { const { data } = await api.put(`/admin/members/${m.id}/lock`); toast.success(data.locked ? "Locked" : "Unlocked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleSuspend = async (m) => {
    try { const { data } = await api.put(`/admin/members/${m.id}/suspend`); toast.success(data.suspended ? "Account suspended" : "Account reactivated"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const reset2fa = async (m) => {
    if (!window.confirm(`Reset 2FA for ${m.name}? They will set up Google Authenticator again on next login.`)) return;
    try { await api.put(`/admin/members/${m.id}/2fa/reset`); toast.success("2FA reset"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={t("members")} subtitle={`${members.length} registered members`} />
      {members.length === 0 ? <Empty label="No members yet." /> : (
        <div className="ec-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-start text-gray-500 border-b border-white/10">
              {["Name", "Email", "Mobile", t("balance"), "Locked Capital", "Unlock Date", "2FA", "Status", "Joined", ""].map((h) => <th key={h} className="text-start font-semibold px-4 py-3">{h}</th>)}
            </tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-white/5" data-testid={`member-row-${m.id}`}>
                  <td className="px-4 py-3 text-white font-medium">{m.name} {m.locked && <Badge className="ms-2 bg-red-500/15 text-red-400 border-red-500/30">locked</Badge>}</td>
                  <td className="px-4 py-3 text-gray-400 keep-ltr">{m.email}</td>
                  <td className="px-4 py-3 text-gray-400 keep-ltr">{m.mobile || "-"}</td>
                  <td className="px-4 py-3 text-gray-200 font-semibold">{fmtPKR(m.balance)}</td>
                  <td className="px-4 py-3 text-gray-300">{fmtPKR(m.locked_capital || 0)}</td>
                  <td className="px-4 py-3 text-gray-500">{m.capital_unlock_date ? fmtDate(m.capital_unlock_date) : "—"}</td>
                  <td className="px-4 py-3" data-testid={`twofa-status-${m.id}`}>
                    {m.twofa_enabled
                      ? <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Enabled</Badge>
                      : <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/30">Disabled</Badge>}
                  </td>
                  <td className="px-4 py-3" data-testid={`status-${m.id}`}>
                    {m.suspended
                      ? <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Suspended</Badge>
                      : <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">Active</Badge>}
                    {m.protection_active && <Badge className="ms-1 bg-blue-500/15 text-blue-400 border-blue-500/30">Protected</Badge>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(m.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setSel(m)} data-testid={`adjust-${m.id}`} className="bg-white/5 border border-white/10 hover:bg-white/10"><Wallet className="w-4 h-4" /></Button>
                      <Button size="sm" onClick={() => toggleSuspend(m)} data-testid={`suspend-${m.id}`} className={m.suspended ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}>{m.suspended ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</Button>
                      <Button size="sm" onClick={() => reset2fa(m)} disabled={!m.twofa_enabled} data-testid={`reset-2fa-${m.id}`} title="Reset 2FA" className="bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-40"><ShieldOff className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="ec-card border-white/10">
          <DialogHeader><DialogTitle className="font-head">{t("adjust_balance")} — {sel?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">{t("current_balance")}: {fmtPKR(sel?.balance)}</p>
            <div><Label>{t("amount")} (+ credit / - debit)</Label><Input data-testid="adjust-amount" type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1" placeholder="5000 or -2000" /></div>
            <div><Label>{t("reason")}</Label><Input data-testid="adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
            <Button onClick={adjust} data-testid="adjust-confirm" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
